import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronRight, Sparkles, Leaf, Heart, Star, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import SpaCard from '../components/SpaCard';
import Subscribe from '../components/Subscribe';

import { ALL_SPAS, AREAS, SPA_TYPES } from '../data/spaData';

const Home = () => {
    const [searchQuery, setSearchQuery] = useState('');

    // Get featured spas (top rated)
    const featuredSpas = useMemo(() => {
        return [...ALL_SPAS]
            .sort((a, b) => b.rating - a.rating)
            .slice(0, 6);
    }, []);

    // Get stats
    const stats = useMemo(() => ({
        totalSpas: ALL_SPAS.length,
        totalAreas: AREAS.length,
        avgRating: (ALL_SPAS.reduce((sum, spa) => sum + spa.rating, 0) / ALL_SPAS.length).toFixed(1)
    }), []);

    // Count spas by type
    const spaCounts = useMemo(() => {
        const counts = {};
        ALL_SPAS.forEach(spa => {
            counts[spa.spa_type] = (counts[spa.spa_type] || 0) + 1;
        });
        return counts;
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            window.location.href = `/spas?search=${encodeURIComponent(searchQuery)}`;
        }
    };

    const spaCategories = [
        { name: 'Luxury Spas', icon: Sparkles, count: spaCounts['Luxury Spa'] || 0, type: 'Luxury Spa', color: 'bg-[#F5E1E4]' },
        { name: 'Ayurvedic', icon: Leaf, count: spaCounts['Ayurvedic Spa'] || 0, type: 'Ayurvedic Spa', color: 'bg-[#D4DED5]' },
        { name: 'Thai Spas', icon: Heart, count: spaCounts['Thai Spa'] || 0, type: 'Thai Spa', color: 'bg-[#F5E1E4]' },
        { name: 'Day Spas', icon: Star, count: spaCounts['Day Spa'] || 0, type: 'Day Spa', color: 'bg-[#D4DED5]' },
    ];

    // Popular areas in Bangalore
    const popularAreas = ['Indiranagar', 'Koramangala', 'MG Road', 'Whitefield', 'Jayanagar', 'HSR Layout'];

    return (
        <div className="min-h-screen noise-overlay" data-testid="home-page">
            {/* Hero Section */}
            <section className="hero-section" style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1920&q=80')`
            }}>
                <div className="hero-overlay"></div>
                <div className="relative z-10 container-wide mx-auto px-4 sm:px-6 lg:px-8 pt-24">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl"
                    >
                        <span className="inline-block px-4 py-1.5 bg-[#722F37]/10 text-[#722F37] text-sm font-medium tracking-widest-custom uppercase rounded-full mb-6">
                            Bangalore's Premier Spa Directory
                        </span>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#4A4E4D] mb-6 leading-tight">
                            Discover Your <br />
                            <span className="text-[#722F37]">Perfect Escape</span>
                        </h1>
                        <p className="font-body text-lg text-[#4A4E4D]/70 mb-8 max-w-xl leading-relaxed-custom">
                            Find the best spas and wellness centers in Bangalore.
                        </p>

                        {/* Search Bar */}
                        <form onSubmit={handleSearch} className="relative max-w-xl">
                            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A4E4D]/40" />
                            <input
                                type="text"
                                placeholder="Search spas by name, area, or type..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-14 pr-36 py-4 bg-white/90 backdrop-blur-sm border border-[#E0DAD0] rounded-full text-[#4A4E4D] placeholder:text-[#4A4E4D]/40 focus:outline-none focus:ring-2 focus:ring-[#722F37]/30 shadow-lg"
                                data-testid="hero-search-input"
                            />
                            <button 
                                type="submit"
                                className="absolute right-2 top-1/2 -translate-y-1/2 btn-gold"
                                data-testid="hero-search-btn"
                            >
                                Search
                            </button>
                        </form>
                    </motion.div>
                </div>
            </section>

            {/* Popular Areas */}
            <section className="section-padding bg-gradient-to-b from-[#F5E1E4]/30 to-[#F9F6F0]">
                <div className="container-wide mx-auto">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <span className="inline-block px-3 py-1 bg-[#722F37]/10 text-[#722F37] text-xs font-medium tracking-widest-custom uppercase rounded-full mb-4">
                            Popular Locations
                        </span>
                        <h2 className="font-heading text-3xl sm:text-4xl text-[#4A4E4D]">
                            Explore by Area
                        </h2>
                    </motion.div>

                    <div className="flex flex-wrap justify-center gap-3">
                        {popularAreas.map((area, index) => (
                            <motion.div
                                key={area}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                                <Link
                                    to={`/spas?area=${encodeURIComponent(area)}`}
                                    className="flex items-center gap-2 px-5 py-3 bg-white rounded-full border border-[#E0DAD0] hover:border-[#722F37] hover:bg-[#F5E1E4] transition-all duration-300 group"
                                >
                                    <MapPin className="w-4 h-4 text-[#722F37]" />
                                    <span className="font-medium text-[#4A4E4D] group-hover:text-[#722F37]">{area}</span>
                                </Link>
                            </motion.div>
                        ))}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: 0.6 }}
                        >
                            <Link
                                to="/spas"
                                className="flex items-center gap-2 px-5 py-3 bg-[#722F37] text-white rounded-full hover:bg-[#8B3D47] transition-all duration-300"
                            >
                                View All Areas
                                <ChevronRight className="w-4 h-4" />
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Categories Section */}
            <section className="section-padding bg-[#F9F6F0]">
                <div className="container-wide mx-auto">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <span className="inline-block px-3 py-1 bg-[#D4DED5]/50 text-[#4A4E4D] text-xs font-medium tracking-widest-custom uppercase rounded-full mb-4">
                            Categories
                        </span>
                        <h2 className="font-heading text-3xl sm:text-4xl text-[#4A4E4D]">
                            Explore by Spa Type
                        </h2>
                    </motion.div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                        {spaCategories.map((category, index) => (
                            <motion.div
                                key={category.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Link
                                    to={`/spas?type=${encodeURIComponent(category.type)}`}
                                    className="block p-6 sm:p-8 bg-white rounded-2xl border border-[#E0DAD0] hover:border-[#722F37] transition-all duration-300 group hover:shadow-lg"
                                    data-testid={`category-${category.type.toLowerCase().replace(' ', '-')}`}
                                >
                                    <div className={`w-12 h-12 rounded-full ${category.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                                        <category.icon className="w-6 h-6 text-[#722F37]" />
                                    </div>
                                    <h3 className="font-heading text-xl text-[#4A4E4D] mb-1 group-hover:text-[#722F37] transition-colors">
                                        {category.name}
                                    </h3>
                                    <p className="text-sm text-[#4A4E4D]/60">{category.count} spas</p>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Spas Section */}
            <section className="section-padding bg-gradient-to-b from-[#F9F6F0] to-[#F5E1E4]/20">
                <div className="container-wide mx-auto">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12"
                    >
                        <div>
                            <span className="inline-block px-3 py-1 bg-[#C8A86B]/10 text-[#C8A86B] text-xs font-medium tracking-widest-custom uppercase rounded-full mb-4">
                                Top Rated
                            </span>
                            <h2 className="font-heading text-3xl sm:text-4xl text-[#4A4E4D]">
                                Featured Spas
                            </h2>
                        </div>
                        <Link 
                            to="/spas" 
                            className="flex items-center gap-2 text-[#722F37] font-medium hover:gap-3 transition-all"
                            data-testid="view-all-spas"
                        >
                            View All Spas
                            <ChevronRight className="w-5 h-5" />
                        </Link>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {featuredSpas.map((spa, index) => (
                            <motion.div
                                key={spa.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <SpaCard spa={spa} variant="featured" />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Subscribe Section */}
            <Subscribe />

            {/* CTA Section */}
            <section className="section-padding">
                <div className="container-wide mx-auto">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.98 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="relative overflow-hidden rounded-3xl bg-[#722F37] p-8 sm:p-12 lg:p-16"
                    >
                        <div className="absolute inset-0 opacity-10">
                            <img 
                                src="https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1200&q=80"
                                alt=""
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="relative z-10 max-w-2xl">
                            <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
                                Can't find the perfect spa?
                            </h2>
                            <p className="text-white/70 text-lg mb-8 leading-relaxed">
                                Tell us your preferences and we'll help you discover hidden gems that match your wellness needs in Bangalore.
                            </p>
                            <Link 
                                to="/contact" 
                                className="inline-flex items-center gap-2 bg-[#C8A86B] text-white px-8 py-4 rounded-full font-medium hover:bg-[#B89A5E] transition-colors"
                                data-testid="cta-contact"
                            >
                                Get Recommendations
                                <ChevronRight className="w-5 h-5" />
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Home;
